import { useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Button,
  Chip,
  DataTable,
  Field,
  Icon,
  IconButton,
  Input,
  Modal,
  Popover,
  Select,
  useToast,
  type Column,
} from '@w7/ui'
import { cmApi } from '@/api/cmApi'
import { authHeaders } from '@/auth/session'
import {
  canBulkTransfer,
  canCreateDownline,
  canCreditTransfer,
  canEditDownline,
  canGameLock,
  canGlobalLock,
  canProductLock,
  canViewCommission,
  isStaffCaller,
} from '@/app/staffAccess'
import { useCmMe } from '@/api/hooks/useCmMe'
import { isConsoleMfaAgentLevel } from '@/features/security/consoleMfaCopy'
import { CmPanel, CmStatus } from '@/features/catalog/CmPanel'
import { BulkSettleDialog } from './BulkSettleDialog'
import { DownlineMfaModal } from './DownlineMfaModal'
import { GameLockModal } from './GameLockModal'
import { MemberEventsModal } from './MemberEventsModal'
import { TransferDialog } from './TransferDialog'
import {
  ROLE_LABEL,
  ROLE_SHORT,
  bpToCommissionPercent,
  downlineListRows,
  filterDownlineRows,
  formatInr,
  formatNodeMoney,
  formatCasinoSharePoints,
  formatSharePoints,
  hierarchyLockUiBlocked,
  isDirectChildOf,
  fromMinorUnits,
  labRateDisplayValue,
  labRateRowsForInfo,
  levelToRole,
  nodeSharePoints,
  parseCurrencyType,
  settleFromPostedPnl,
  type DownlineListScope,
  type HierarchyNode,
  type Role,
} from './hierarchy'
import './hierarchy.css'

const ROLE_FILTERS: { value: Role | 'all'; label: string }[] = [
  { value: 'all', label: 'All roles' },
  { value: 'triple-master', label: 'Triple Master' },
  { value: 'double-master', label: 'Double Master' },
  { value: 'super-master', label: 'Super Master' },
  { value: 'master', label: 'Master' },
  { value: 'player', label: 'Member' },
]

type SettlementFilter = 'all' | 'collect' | 'pay'

type TransferTarget = {
  account: HierarchyNode
  direction: 'give' | 'take'
  amount?: number
}

type GlobalLockConfirm =
  | { kind: 'book'; unlocking: boolean }
  | { kind: 'member'; row: HierarchyNode; unlocking: boolean }

/**
 * Lab Member List parity for CM: Direct children show Take / Give from
 * balanceUp (`postedPnl`), click opens TransferDialog (Agent-UI parity), Bulk
 * transfer confirms amounts first. Deeper downline is browse-only.
 */
export function DownlineListPage() {
  const navigate = useNavigate()

  const me = useCmMe()
  const toast = useToast()
  // Wait for /v1/me so staff lock *write* grants are known (view is always allowed).
  const meReady = me.isSuccess
  const allowProductLock = meReady && canProductLock(me.data?.caller)
  const allowGameLock = meReady && canGameLock(me.data?.caller)
  const allowGlobalLock = meReady && canGlobalLock(me.data?.caller)
  const allowEditDownline = meReady && canEditDownline(me.data?.caller)
  const allowCreateDownline = meReady && canCreateDownline(me.data?.caller)
  const allowCreditTransfer = meReady && canCreditTransfer(me.data?.caller)
  const allowBulkTransfer = meReady && canBulkTransfer(me.data?.caller)
  const allowViewCommission = meReady && canViewCommission(me.data?.caller)
  const ownerCm = meReady && Boolean(me.data) && !isStaffCaller(me.data?.caller)

  const ensureGlobalLock = async (): Promise<boolean> => {
    const fresh = await me.refetch()
    if (canGlobalLock(fresh.data?.caller)) return true
    toast.error('Missing access grant: Global lock / unlock')
    return false
  }
  const qc = useQueryClient()
  const [scope, setScope] = useState<DownlineListScope>('direct')
  const [q, setQ] = useState('')
  const [role, setRole] = useState<Role | 'all'>('all')
  const [settlement, setSettlement] = useState<SettlementFilter>('all')
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set())
  const [transfer, setTransfer] = useState<TransferTarget | null>(null)
  const [bulkOpen, setBulkOpen] = useState(false)
  const [eventsFor, setEventsFor] = useState<HierarchyNode | null>(null)
  const [gameLockFor, setGameLockFor] = useState<HierarchyNode | null>(null)
  const [mfaFor, setMfaFor] = useState<HierarchyNode | null>(null)
  const [lockConfirm, setLockConfirm] = useState<GlobalLockConfirm | null>(null)
  
  const self = useQuery({
    queryKey: ['cm', 'hierarchy', 'self'],
    queryFn: () => cmApi.hierarchySelf(authHeaders()),
  })

  const tree = useQuery({
    queryKey: ['cm', 'hierarchy', 'tree'],
    queryFn: () => cmApi.hierarchyTree(authHeaders()),
  })

  /**
   * Owner CM — 2FA on/off map for the shield column.
   * Hierarchy `agent_nodes.id` ≠ `player.users.id` — members match by username
   * (same join as smart-users cron). Agents match by agentId.
   */
  const mfaStatusQ = useQuery({
    queryKey: ['cm', 'console-mfa', 'downline-status'],
    enabled: ownerCm,
    staleTime: 60_000,
    queryFn: async () => {
      type St = {
        mfaEnabled: boolean
        setupRequired: boolean
        found: boolean
        kind: 'agent' | 'player'
        /** Portal user id when kind=player (for manage modal / set). */
        portalUserId?: string
      }
      const byId = new Map<string, St>()
      const byUsername = new Map<string, St>()
      const pageSize = 50
      const maxPages = 20

      const pullPages = async <T,>(
        fetchPage: (page: number) => Promise<{ rows: T[]; total: number; pageSize: number }>,
        apply: (row: T) => void,
      ) => {
        const first = await fetchPage(1)
        for (const row of first.rows) apply(row)
        const totalPages = Math.min(
          maxPages,
          Math.max(1, Math.ceil((first.total || 0) / (first.pageSize || pageSize))),
        )
        if (totalPages <= 1) return
        const rest = await Promise.all(
          Array.from({ length: totalPages - 1 }, (_, i) => fetchPage(i + 2)),
        )
        for (const res of rest) for (const row of res.rows) apply(row)
      }

      await Promise.all([
        pullPages(
          (page) =>
            cmApi.consoleMfaList(authHeaders(), { scope: 'tree', page, pageSize }),
          (row) => {
            byId.set(row.agentId.toLowerCase(), {
              mfaEnabled: row.mfaEnabled,
              setupRequired: row.setupRequired,
              found: row.found,
              kind: 'agent',
            })
          },
        ),
        pullPages(
          (page) => cmApi.playerConsoleMfaList(authHeaders(), { page, pageSize }),
          (row) => {
            const st: St = {
              mfaEnabled: row.mfaEnabled,
              setupRequired: row.setupRequired === true,
              found: row.found,
              kind: 'player',
              portalUserId: row.playerId,
            }
            byId.set(row.playerId.toLowerCase(), st)
            const uname = (row.username || '').trim().toLowerCase()
            if (uname) byUsername.set(uname, st)
          },
        ),
      ])
      return { byId, byUsername }
    },
  })

  const mfaStatusFor = (row: HierarchyNode) => {
    const map = mfaStatusQ.data
    if (!map) return undefined
    const byId = map.byId.get(row.id.toLowerCase())
    if (byId) return byId
    const uname = (row.username || row.name || '').trim().toLowerCase()
    if (!uname) return undefined
    return map.byUsername.get(uname)
  }

  const rootId = self.data?.node.id ?? tree.data?.rootId
  const rootNode = self.data?.node
  const nodes = tree.data?.nodes ?? []
  const bookLocked = rootNode?.globalLock === true
  const nodeById = useMemo(() => new Map(nodes.map((n) => [n.id, n])), [nodes])

  const invalidateHierarchy = () => {
    void qc.invalidateQueries({ queryKey: ['cm', 'hierarchy'] })
  }

  const globalLockMut = useMutation({
    mutationFn: () => cmApi.hierarchyGlobalLock(authHeaders()),
    onSuccess: (res) => {
      toast.success(res.message)
      setLockConfirm(null)
      invalidateHierarchy()
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : 'Global lock failed')
    },
  })

  const memberLockMut = useMutation({
    mutationFn: (memberId: string) => cmApi.hierarchySingleMemberLock(authHeaders(), memberId),
    onSuccess: (res) => {
      toast.success(res.message)
      setLockConfirm(null)
      invalidateHierarchy()
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : 'Global lock failed')
    },
  })

  const lockBusy = globalLockMut.isPending || memberLockMut.isPending

  const rows = useMemo(() => {
    const base = downlineListRows(nodes, rootId, scope)
    return filterDownlineRows(base, { q, role, settlement })
  }, [nodes, rootId, scope, q, role, settlement])

  const openSettle = (row: HierarchyNode) => {
    const move = settleFromPostedPnl(row.postedPnl, row.currencyType)
    if (!move) return
    setTransfer({
      account: row,
      direction: move.direction,
      amount: move.amountDisplay,
    })
  }

  const selectedRows = useMemo(
    () =>
      rows.filter(
        (r) =>
          selectedIds.has(r.id) &&
          isDirectChildOf(r, rootId) &&
          settleFromPostedPnl(r.postedPnl, r.currencyType),
      ),
    [rows, selectedIds, rootId],
  )

  const columns: Column<HierarchyNode>[] = useMemo(
    () => [
      ...(allowBulkTransfer
        ? [
            {
              id: 'select',
              header: '',
              width: 44,
              cell: (row: HierarchyNode) => {
                if (
                  !isDirectChildOf(row, rootId) ||
                  !settleFromPostedPnl(row.postedPnl, row.currencyType)
                ) {
                  return null
                }
                const checked = selectedIds.has(row.id)
                return (
                  <input
                    type="checkbox"
                    aria-label={`Select ${row.name} for bulk settle`}
                    checked={checked}
                    onClick={(event) => event.stopPropagation()}
                    onChange={() => {
                      setSelectedIds((prev) => {
                        const next = new Set(prev)
                        if (next.has(row.id)) next.delete(row.id)
                        else next.add(row.id)
                        return next
                      })
                    }}
                  />
                )
              },
            } satisfies Column<HierarchyNode>,
          ]
        : []),
      {
        id: 'name',
        header: 'Agent / member',
        cell: (row) => {
          const r = levelToRole(row.level)
          return (
            <div className="cm-dl-name">
              <span className="cm-hier-role" title={ROLE_LABEL[r]}>
                {ROLE_SHORT[r]}
              </span>
              <div>
                <button
                  type="button"
                  className="w7-link cm-dl-member-link"
                  onClick={() => navigate(`/member/${row.id}`)}
                >
                  <strong>{row.name}</strong>
                </button>
                {row.username ? (
                  <button
                    type="button"
                    className="cm-hier-meta w7-link cm-dl-member-link"
                    onClick={() => navigate(`/member/${row.id}`)}
                  >
                    {row.username}
                  </button>
                ) : null}
              </div>
            </div>
          )
        },
        sortValue: (row) => row.name.toLowerCase(),
      },
      {
        id: 'role',
        header: 'Role',
        hideBelow: 720,
        cell: (row) => ROLE_LABEL[levelToRole(row.level)],
        sortValue: (row) => row.level,
      },
      {
        id: 'credit',
        header: 'Credit limit',
        numeric: true,
        cell: (row) => formatInr(fromMinorUnits(row.creditLimit)),
        sortValue: (row) => fromMinorUnits(row.creditLimit),
      },
      {
        id: 'balance',
        header: 'Live balance',
        numeric: true,
        hideBelow: 900,
        cell: (row) => formatInr(fromMinorUnits(row.liveBalance ?? row.balance)),
        sortValue: (row) => fromMinorUnits(row.liveBalance ?? row.balance),
      },
      {
        id: 'take',
        header: 'Take',
        numeric: true,
        cell: (row) => {
          const move = settleFromPostedPnl(row.postedPnl, row.currencyType)
          if (!move || move.direction !== 'take') {
            return <span className="cm-hint">—</span>
          }
          const label = formatNodeMoney(move.amountMinor, row.currencyType)
          if (!isDirectChildOf(row, rootId) || !allowCreditTransfer) return label
          return (
            <button
              type="button"
              className="cm-settle-amt is-take"
              aria-label={`Take ${label} from ${row.name}`}
              onClick={(event) => {
                // DataTable row onClick navigates to /member/:id — must not steal Take.
                event.stopPropagation()
                openSettle(row)
              }}
            >
              {label}
            </button>
          )
        },
        sortValue: (row) => {
          const m = settleFromPostedPnl(row.postedPnl, row.currencyType)
          return m?.direction === 'take' ? m.amountDisplay : 0
        },
      },
      {
        id: 'give',
        header: 'Give',
        numeric: true,
        cell: (row) => {
          const move = settleFromPostedPnl(row.postedPnl, row.currencyType)
          if (!move || move.direction !== 'give') {
            return <span className="cm-hint">—</span>
          }
          const label = formatNodeMoney(move.amountMinor, row.currencyType)
          if (!isDirectChildOf(row, rootId) || !allowCreditTransfer) return label
          return (
            <button
              type="button"
              className="cm-settle-amt is-give"
              aria-label={`Give ${label} to ${row.name}`}
              onClick={(event) => {
                // DataTable row onClick navigates to /member/:id — must not steal Give.
                event.stopPropagation()
                openSettle(row)
              }}
            >
              {label}
            </button>
          )
        },
        sortValue: (row) => {
          const m = settleFromPostedPnl(row.postedPnl, row.currencyType)
          return m?.direction === 'give' ? m.amountDisplay : 0
        },
      },
      {
        id: 'exposure',
        header: 'Exposure',
        numeric: true,
        hideBelow: 900,
        cell: (row) => formatInr(fromMinorUnits(row.exposure)),
        sortValue: (row) => fromMinorUnits(row.exposure),
      },
      {
        id: 'share',
        header: 'My share',
        hideBelow: 1100,
        cell: (row) => {
          const share = nodeSharePoints(row)
          const ccy = parseCurrencyType(row.currencyType)
          return `${formatSharePoints(share.sportsbook, ccy)} / ${formatCasinoSharePoints(share.casino, ccy)}`
        },
      },
      ...(allowViewCommission
        ? [
            {
              id: 'commission',
              header: 'Fancy / BM',
              hideBelow: 1100,
              cell: (row: HierarchyNode) =>
                `${bpToCommissionPercent(row.commissionBp ?? 0).toFixed(2)}% / ${bpToCommissionPercent(row.commissionBookmakerBp ?? row.commissionBp ?? 0).toFixed(2)}%`,
            } satisfies Column<HierarchyNode>,
          ]
        : []),
      {
        id: 'status',
        header: 'Status',
        width: 100,
        cell: (row) => {
          const s = row.status ?? 'active'
          const tone = s === 'active' ? 'info' : s === 'suspended' ? 'warn' : 'neutral'
          return (
            <Chip tone={tone} size="sm">
              {s}
            </Chip>
          )
        },
      },
      ...(ownerCm
        ? [
            {
              id: 'console-2fa',
              header: '2FA',
              width: 72,
              align: 'center' as const,
              cell: (row: HierarchyNode) => {
                const agent = isConsoleMfaAgentLevel(row.level)
                const player = row.level === 6
                if (!agent && !player) {
                  return <span className="cm-hint">—</span>
                }
                // Never block on MFA status fetch — tree rows are ready; shield stays
                // clickable. Members resolve via username (hierarchy id ≠ player.users id).
                const st = mfaStatusFor(row)
                const label = agent ? 'Console 2FA' : 'Player 2FA'
                let title: string
                let tone: 'is-on' | 'is-off' | 'is-unknown' | 'is-pending'
                if (!mfaStatusQ.data) {
                  title = `${label} — click to manage`
                  tone = 'is-unknown'
                } else if (!st?.found) {
                  title = agent
                    ? 'No Keycloak console login — click to manage'
                    : 'No portal factors yet — click for details'
                  tone = 'is-unknown'
                } else if (st.mfaEnabled) {
                  title = st.setupRequired
                    ? `${label} on · setup still forced next login — click to manage`
                    : `${label} on — click to manage`
                  tone = 'is-on'
                } else if (st.setupRequired) {
                  title = `${label} off · setup required next login — click to manage`
                  tone = 'is-pending'
                } else {
                  title = `${label} off — click to manage`
                  tone = 'is-off'
                }
                return (
                  <IconButton
                    name="shield"
                    size="sm"
                    className={`cm-mfa-status ${tone}`}
                    label={`${row.name}: ${title}`}
                    title={title}
                    data-stop-row-click
                    onClick={(event) => {
                      // DataTable row onClick navigates to /member/:id — must not steal 2FA.
                      event.preventDefault()
                      event.stopPropagation()
                      setMfaFor(row)
                    }}
                  />
                )
              },
            } satisfies Column<HierarchyNode>,
          ]
        : []),
      {
        id: 'info',
        header: 'Info',
        width: 72,
        cell: (row) => {
          const parent = row.parentId ? nodeById.get(row.parentId) : null
          return <RateInfoCell row={row} parent={parent} />
        },
      },
      ...(bookLocked && meReady
        ? [
            {
              id: 'global-lock',
              header: 'Global Lock',
              width: 88,
              align: 'center' as const,
              cell: (row: HierarchyNode) => {
                const closed = hierarchyLockUiBlocked(row.status)
                const locked = row.globalLock === true
                const viewOnly = !allowGlobalLock
                return (
                  <button
                    type="button"
                    className={`cm-events-lock${locked ? ' is-locked' : ' is-unlocked'}${closed || viewOnly ? ' is-muted' : ''}`}
                    disabled={closed || lockBusy || viewOnly}
                    aria-label={
                      closed
                        ? `${row.name} is closed`
                        : viewOnly
                          ? locked
                            ? `${row.name} globally locked (view only)`
                            : `${row.name} not globally locked (view only)`
                          : locked
                            ? `Unlock ${row.name} globally`
                            : `Lock ${row.name} globally`
                    }
                    title={
                      closed
                        ? 'Closed'
                        : viewOnly
                          ? locked
                            ? 'Globally locked (view only)'
                            : 'Not under global lock (view only)'
                          : locked
                            ? 'Globally locked — click to except this account'
                            : 'Excepted from global lock — click to lock again'
                    }
                    onClick={(event) => {
                      event.stopPropagation()
                      if (closed || viewOnly) return
                      void ensureGlobalLock().then((ok) => {
                        if (ok) setLockConfirm({ kind: 'member', row, unlocking: locked })
                      })
                    }}
                  >
                    <Icon name={locked ? 'lock' : 'unlock'} size={18} />
                  </button>
                )
              },
            } satisfies Column<HierarchyNode>,
          ]
        : []),
      ...(meReady
        ? [
            {
              id: 'lock',
              header: 'Lock',
              width: 72,
              align: 'center' as const,
              cell: (row: HierarchyNode) => {
                // Lab: Inactive stays clickable even when sportsbook already locked.
                const closed = hierarchyLockUiBlocked(row.status)
                const sportsbookLocked = row.gameLocks?.sportsbook?.status === true
                return (
                  <IconButton
                    name={closed || sportsbookLocked ? 'lock' : 'unlock'}
                    size="sm"
                    disabled={closed}
                    label={
                      closed
                        ? `${row.name} is closed`
                        : `Games lock for ${row.name}`
                    }
                    title={
                      closed
                        ? 'Closed'
                        : allowGameLock
                          ? 'Lock or disable games for this user'
                          : 'View game locks for this user'
                    }
                    onClick={(event) => {
                      event.stopPropagation()
                      if (closed) return
                      setGameLockFor(row)
                    }}
                  />
                )
              },
            } satisfies Column<HierarchyNode>,
          ]
        : []),
      {
        id: 'event',
        header: 'Events',
        width: 72,
        align: 'center',
        cell: (row) => (
          <IconButton
            name="calendar"
            size="sm"
            label={`Events for ${row.name}`}
            title={
              allowProductLock
                ? 'Enable or disable matches for this user'
                : 'View match enable/disable status for this user'
            }
            onClick={(event) => {
              event.stopPropagation()
              setEventsFor(row)
            }}
          />
        ),
      },
      {
        id: 'actions',
        header: 'Actions',
        width: 160,
        cell: (row) => {
          const isDirect = isDirectChildOf(row, rootId)
          return (
            <div className="cm-dl-actions">
              {isDirect && allowEditDownline ? (
                <Button
                  type="button"
                  size="sm"
                  variant="primary"
                  onClick={(event) => {
                    event.stopPropagation()
                    navigate(`/hierarchy/edit/${row.id}`)
                  }}
                >
                  Edit
                </Button>
              ) : (
                <span className="cm-hint">—</span>
              )}
            </div>
          )
        },
      },
    ],
    [
      navigate,
      nodeById,
      rootId,
      selectedIds,
      bookLocked,
      lockBusy,
      meReady,
      ownerCm,
      mfaStatusQ.data,
      allowProductLock,
      allowGlobalLock,
      allowGameLock,
      allowEditDownline,
      allowBulkTransfer,
      allowCreditTransfer,
      allowViewCommission,
    ],
  )

  const selectedCount = selectedIds.size

  return (
    <CmPanel
      title="Downline list"
      subtitle="Lab Member List for CM — Take / Give settle direct children (TM)"
      actions={
        <>
          <Button type="button" variant="secondary" onClick={() => navigate('/hierarchy')}>
            Tree view
          </Button>
          <Button
            type="button"
            variant="secondary"
            onClick={() => {
              void qc.refetchQueries({ queryKey: ['cm', 'hierarchy'] })
              if (ownerCm) void qc.refetchQueries({ queryKey: ['cm', 'console-mfa', 'downline-status'] })
            }}
          >
            Refresh
          </Button>
          {allowGlobalLock ? (
            <Button
              type="button"
              variant={bookLocked ? 'danger' : 'secondary'}
              onClick={() => {
                void ensureGlobalLock().then((ok) => {
                  if (ok) setLockConfirm({ kind: 'book', unlocking: bookLocked })
                })
              }}
            >
              {bookLocked ? 'Global Unlock' : 'Global Lock'}
            </Button>
          ) : meReady && bookLocked ? (
            <Button type="button" variant="danger" disabled title="View only — missing Global lock / unlock grant">
              Globally locked
            </Button>
          ) : null}
          {allowCreateDownline ? (
            <Button type="button" variant="primary" onClick={() => navigate('/hierarchy/create')}>
              Create Triple Master
            </Button>
          ) : null}
        </>
      }
    >
      {self.isError || tree.isError ? (
        <CmStatus
          value={(self.error as Error)?.message || (tree.error as Error)?.message || 'Failed'}
          label="Could not load downline"
        />
      ) : null}

      <div className="cm-dl-toolbar" data-testid="downline-list-toolbar">
        <div className="cm-dl-scope" role="group" aria-label="List scope">
          <button
            type="button"
            className={`cm-dl-scope-btn${scope === 'direct' ? ' is-active' : ''}`}
            onClick={() => setScope('direct')}
          >
            Direct only
          </button>
          <button
            type="button"
            className={`cm-dl-scope-btn${scope === 'all' ? ' is-active' : ''}`}
            onClick={() => setScope('all')}
          >
            Whole downline
          </button>
        </div>
        <div className="cm-dl-scope" role="group" aria-label="Settlement filter">
          <button
            type="button"
            className={`cm-dl-scope-btn${settlement === 'all' ? ' is-active' : ''}`}
            onClick={() => setSettlement('all')}
          >
            All
          </button>
          <button
            type="button"
            className={`cm-dl-scope-btn${settlement === 'collect' ? ' is-active' : ''}`}
            onClick={() => setSettlement(settlement === 'collect' ? 'all' : 'collect')}
          >
            Take outstanding
          </button>
          <button
            type="button"
            className={`cm-dl-scope-btn${settlement === 'pay' ? ' is-active' : ''}`}
            onClick={() => setSettlement(settlement === 'pay' ? 'all' : 'pay')}
          >
            Give outstanding
          </button>
        </div>
        <Field label="Search">
          {({ id, describedBy }) => (
            <Input
              id={id}
              aria-describedby={describedBy}
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Name or username"
            />
          )}
        </Field>
        <Field label="Role">
          {({ id, describedBy }) => (
            <Select
              id={id}
              aria-describedby={describedBy}
              value={role}
              onChange={(e) => setRole(e.target.value as Role | 'all')}
              options={ROLE_FILTERS.map((opt) => ({ value: opt.value, label: opt.label }))}
            />
          )}
        </Field>
      </div>

      {allowBulkTransfer && selectedCount > 0 ? (
        <div className="cm-dl-bulk" role="region" aria-label="Bulk settlement">
          <span>
            {selectedCount} selected — settle Take/Give to zero (Lab Bulk transfer)
          </span>
          <Button
            type="button"
            variant="primary"
            onClick={() => {
              if (!selectedRows.length) return
              setBulkOpen(true)
            }}
          >
            Bulk transfer
          </Button>
          <Button type="button" variant="secondary" onClick={() => setSelectedIds(new Set())}>
            Clear
          </Button>
        </div>
      ) : null}

      <p className="cm-hint">
        {scope === 'direct'
          ? 'Direct children only — click Take / Give to open the settle dialog (amount, max, before/after).'
          : 'Entire subtree. Take / Give settle only on your direct children (CM → TM or CM → Member).'}{' '}
        Showing {rows.length} row{rows.length === 1 ? '' : 's'}.
      </p>

      <div data-testid="downline-list-table">
        <DataTable
          label="Downline accounts"
          rowKey={(row) => row.id}
          columns={columns}
          rows={rows}
          loading={tree.isLoading || self.isLoading}
          onRowClick={(row) => navigate(`/member/${row.id}`)}
          empty={
            <p className="cm-hint">
              No accounts match — create a Triple Master, or clear filters.
            </p>
          }
        />
      </div>

      {transfer && rootNode && isDirectChildOf(transfer.account, rootId) ? (
        <TransferDialog
          self={rootNode}
          account={transfer.account}
          initialDirection={transfer.direction}
          initialAmount={transfer.amount}
          onClose={() => setTransfer(null)}
        />
      ) : null}

      {bulkOpen ? (
        <BulkSettleDialog
          selected={selectedRows}
          onClose={() => {
            setBulkOpen(false)
            setSelectedIds(new Set())
          }}
        />
      ) : null}

      {eventsFor ? (
        <MemberEventsModal
          userId={eventsFor.id}
          username={eventsFor.username || eventsFor.name}
          onClose={() => setEventsFor(null)}
        />
      ) : null}

      {gameLockFor ? (
        <GameLockModal
          node={nodes.find((n) => n.id === gameLockFor.id) ?? gameLockFor}
          bookLocked={bookLocked}
          readOnly={!allowGameLock}
          onClose={() => setGameLockFor(null)}
        />
      ) : null}

      {mfaFor ? (
        <DownlineMfaModal row={mfaFor} onClose={() => setMfaFor(null)} />
      ) : null}

      {lockConfirm ? (
        <Modal
          open
          onClose={() => {
            if (!lockBusy) setLockConfirm(null)
          }}
          title={lockConfirm.unlocking ? 'Global Unlock' : 'Global Lock'}
          tone="danger"
          footer={
            <>
              <Button
                type="button"
                variant="secondary"
                disabled={lockBusy}
                onClick={() => setLockConfirm(null)}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="danger"
                disabled={lockBusy}
                onClick={() => {
                  if (lockConfirm.kind === 'book') {
                    globalLockMut.mutate()
                    return
                  }
                  memberLockMut.mutate(lockConfirm.row.id)
                }}
              >
                {lockConfirm.unlocking ? 'Unlock' : 'Lock'}
              </Button>
            </>
          }
        >
          <p>
            {lockConfirm.kind === 'book'
              ? `Are you sure you want to ${lockConfirm.unlocking ? 'Unlock' : 'Lock'} globally?`
              : `Are you sure you want to ${lockConfirm.unlocking ? 'Unlock' : 'Lock'} ${lockConfirm.row.username || lockConfirm.row.name} globally?`}
          </p>
          {lockConfirm.kind === 'book' ? (
            <p className="cm-hint">
              {lockConfirm.unlocking
                ? 'Sportsbook, casino, and card-game betting reopen for the downline (except anyone you lock again).'
                : 'Betting is locked for the whole downline. You can except individual rows from the Global Lock column.'}
            </p>
          ) : (
            <p className="cm-hint">
              {lockConfirm.unlocking
                ? 'This account and its downline can place bets while the book stays globally locked.'
                : 'This account returns to the book-wide lock.'}
            </p>
          )}
        </Modal>
      ) : null}
    </CmPanel>
  )
}

/**
 * Lab view-member Info — portaled Popover (table cells clip absolute CSS panels).
 */
export function RateInfoCell({
  row,
  parent,
}: {
  row: HierarchyNode
  parent?: HierarchyNode | null
}) {
  const [open, setOpen] = useState(false)
  const anchor = useRef<HTMLButtonElement>(null)
  const rates = labRateRowsForInfo(row, parent)
  const role = ROLE_LABEL[levelToRole(row.level)]
  const ccy = parseCurrencyType(row.currencyType)
  return (
    <div className="cm-rate-info">
      <IconButton
        ref={anchor}
        name="info"
        size="sm"
        label={`Rate info for ${row.name}`}
        onClick={(event) => {
          event.stopPropagation()
          setOpen((v) => !v)
        }}
      />
      <Popover
        open={open}
        onClose={() => setOpen(false)}
        anchorRef={anchor}
        label={`Rates for ${row.name}`}
        align="end"
        width={220}
      >
        <div className="cm-rate-info-head">
          {role} — {ccy}
        </div>
        {rates.length === 0 ? (
          <div className="cm-hint">No rates</div>
        ) : (
          rates.map((game) => (
            <div key={game._id} className="cm-rate-info-row">
              <span>{game.game.name}</span>
              <span>{labRateDisplayValue(game, row.currencyType).toFixed(ccy === 'INR' ? 2 : 0)}</span>
            </div>
          ))
        )}
      </Popover>
    </div>
  )
}
